import { reactive } from 'vue';
import { SYNC_DATA_VERSION, validateWord, validateSnapshotPage, validateDeltaPage } from './syncValidation';
import { api } from './api';
import { cloneValue, deepEqual, mergeLocalOverRemote } from './syncMerge';
import { selectMutationBatch } from './localSyncScheduling';
import type {
  OutboxRecord,
  SyncCatalogs,
  SyncChangeEvent,
  SyncPreferencesRecord,
  SyncWordRecord,
} from '../types';

const DB_PREFIX = 'geobu-sync';
const DB_VERSION = 1;

const POLL_VISIBLE_MS = 60_000;
const POLL_HIDDEN_MS = 300_000;
const OFFLINE_PROBE_MS = 1_000;
const INITIAL_RETRY_MS = 5_000;
const MAX_RETRY_MS = 60_000;

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `mut_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbTransactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction aborted'));
  });
}

export class LocalSyncService {
  private db: IDBDatabase | null = null;
  private currentUserId: string | null = null;
  private channel: BroadcastChannel | null = null;
  private isLeader = false;
  private leaderAbort: AbortController | null = null;
  private activeRequestAbort: AbortController | null = null;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay = INITIAL_RETRY_MS;
  private flushing = false;
  private flushAgain = false;
  private polling = false;

  public readonly state = reactive({
    connection: (typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'online') as
      | 'online'
      | 'offline'
      | 'unreachable'
      | 'auth_required',
    realtime: 'reconnecting' as 'connected' | 'reconnecting' | 'unavailable',
    pendingWords: 0,
    pendingPreferences: 0,
    failed: 0,
    bootstrapping: false,
    status: (typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'idle') as
      | 'idle'
      | 'syncing'
      | 'synced'
      | 'pending'
      | 'offline'
      | 'error',
    pending: 0,
    totalWords: 0,
    lastSyncedAt: 0,
    error: '',
  });

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        this.state.connection = 'online';
        this.retryDelay = INITIAL_RETRY_MS;
        this.recomputeStatus();
        if (this.isLeader) {
          void this.flush().then(() => this.poll());
        }
      });

      window.addEventListener('offline', () => {
        this.state.connection = 'offline';
        this.state.realtime = 'reconnecting';
        this.activeRequestAbort?.abort();
        this.recomputeStatus();
        this.scheduleNextPoll();
      });

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.isLeader && this.state.connection !== 'offline') {
          void this. flush().then(() => this.poll());
        }
        this.scheduleNextPoll();
      });
    }
  }

  public hasPending(): boolean {
    return this.state.pending > 0;
  }

  /**
   * Checks whether any local IndexedDB database on this device already contains
   * cached flashcards so the learner can study offline even if their token expired.
   */
  public async discoverLocalUserWithWords(preferredUserId?: string): Promise<{
    userId: string;
    wordsCount: number;
  } | null> {
    if (typeof indexedDB === 'undefined') return null;

    const candidates: string[] = [];
    if (preferredUserId) candidates.push(String(preferredUserId));
    const lastUser = api.getLastOfflineUser();
    if (lastUser?.user_id && !candidates.includes(String(lastUser.user_id))) {
      candidates.push(String(lastUser.user_id));
    }

    try {
      if (typeof indexedDB.databases === 'function') {
        const dbs = await indexedDB.databases();
        for (const info of dbs) {
          if (info.name && info.name.startsWith(`${DB_PREFIX}:`)) {
            const uid = info.name.slice(`${DB_PREFIX}:`.length);
            if (uid && !candidates.includes(uid)) {
              candidates.push(uid);
            }
          }
        }
      }
    } catch {
      // indexedDB.databases() not supported in some environments
    }

    if (!candidates.includes('anonymous')) {
      candidates.push('anonymous');
    }

    for (const uid of candidates) {
      try {
        if (this.currentUserId === uid && this.db) {
          const tx = this.db.transaction('words', 'readonly');
          const count = await idbRequest<number>(tx.objectStore('words').count());
          if (count > 0) {
            return { userId: uid, wordsCount: count };
          }
          continue;
        }

        const tempDb = await this.openDatabase(uid);
        try {
          const tx = tempDb.transaction('words', 'readonly');
          const count = await idbRequest<number>(tx.objectStore('words').count());
          if (count > 0) {
            return { userId: uid, wordsCount: count };
          }
        } finally {
          tempDb.close();
        }
      } catch {
        // ignore
      }
    }
    return null;
  }

  private recomputeStatus() {
    const pendingTotal = this.state.pendingWords + this.state.pendingPreferences;
    this.state.pending = pendingTotal;

    if (this.state.connection === 'offline') {
      this.state.status = 'offline';
    } else if (this.state.connection === 'auth_required' || this.state.error || this.state.failed > 0) {
      this.state.status = 'error';
    } else if (this.state.bootstrapping || this.flushing || this.polling) {
      this.state.status = 'syncing';
    } else if (pendingTotal > 0 || this.state.connection === 'unreachable') {
      this.state.status = 'pending';
    } else {
      this.state.status = 'synced';
    }
  }

  public async start(userId: string): Promise<void> {
    const cleanId = String(userId || 'anonymous');
    if (this.currentUserId === cleanId && this.db) {
      await this.refreshCounts();
      if (this.isLeader && navigator.onLine) {
        void this.syncNow();
      }
      return;
    }

    this.stop();
    this.currentUserId = cleanId;
    this.db = await this.openDatabase(cleanId);
    await this.refreshCounts();

    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(`${DB_PREFIX}:${cleanId}`);
      this.channel.onmessage = async (ev) => {
        const data = ev.data || {};
        if (data.type === 'outbox' && this.isLeader) {
          await this.refreshCounts();
          void this.flush();
        } else if (data.type === 'changed') {
          await this.refreshCounts();
          window.dispatchEvent(new CustomEvent('geobu-sync-changed', { detail: data }));
        }
      };
    }

    this.acquireLeadership(cleanId);
  }

  public stop(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.activeRequestAbort?.abort();
    this.leaderAbort?.abort();
    this.isLeader = false;
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    if (this.db) {
      this.db.close();
      this.db = null;
    }
    this.currentUserId = null;
  }

  private openDatabase(userId: string): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(`${DB_PREFIX}:${userId}`, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains('words')) {
          const wordsStore = db.createObjectStore('words', { keyPath: 'id' });
          wordsStore.createIndex('dictionary_id', 'dictionary_id', { unique: false });
          wordsStore.createIndex('language_code', 'language_code', { unique: false });
          wordsStore.createIndex('part_of_speech_id', 'part_of_speech_id', { unique: false });
          wordsStore.createIndex('stars', 'stars', { unique: false });
          wordsStore.createIndex('is_learned', 'is_learned', { unique: false });
        }
        if (!db.objectStoreNames.contains('remote_words')) {
          db.createObjectStore('remote_words', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('preferences')) {
          db.createObjectStore('preferences', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('remote_preferences')) {
          db.createObjectStore('remote_preferences', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('catalogs')) {
          db.createObjectStore('catalogs', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('outbox')) {
          const outboxStore = db.createObjectStore('outbox', { keyPath: 'mutation_id' });
          outboxStore.createIndex('entity_id', 'entity_id', { unique: false });
        }
        if (!db.objectStoreNames.contains('tombstones')) {
          db.createObjectStore('tombstones', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }

  private acquireLeadership(userId: string) {
    this.leaderAbort = new AbortController();
    const signal = this.leaderAbort.signal;

    const onBecomeLeader = async () => {
      if (signal.aborted) return;
      this.isLeader = true;
      const bootstrapped = (await this.getMeta<number>('sync_data_version')) === SYNC_DATA_VERSION;
      if (!bootstrapped) {
        await this.bootstrap();
      } else {
        await this.flush();
        await this.poll();
      }
      this.scheduleNextPoll();
    };

    if (typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks?.request) {
      navigator.locks
        .request(`${DB_PREFIX}-leader:${userId}`, { signal }, async () => {
          await onBecomeLeader();
          return new Promise<void>((resolve) => {
            signal.addEventListener('abort', () => resolve(), { once: true });
          });
        })
        .catch(() => {
          // Lock aborted or unsupported
        });
    } else {
      void onBecomeLeader();
    }
  }

  private scheduleNextPoll() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    if (!this.isLeader) return;

    let delay = POLL_VISIBLE_MS;
    if (this.state.connection === 'offline') {
      delay = OFFLINE_PROBE_MS;
    } else if (this.state.connection === 'unreachable' || this.state.error) {
      const jitter = 0.8 + Math.random() * 0.4;
      delay = Math.min(MAX_RETRY_MS, Math.round(this.retryDelay * jitter));
    } else if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      delay = POLL_HIDDEN_MS;
    }

    this.pollTimer = setTimeout(async () => {
      if (!this.isLeader) return;
      if (this.state.connection === 'offline') {
        if (typeof navigator !== 'undefined' && navigator.onLine) {
          this.state.connection = 'online';
          this.recomputeStatus();
          await this.flush();
          await this.poll();
        }
        this.scheduleNextPoll();
        return;
      }
      await this.flush();
      await this.poll();
      this.scheduleNextPoll();
    }, delay);
  }

  private async getMeta<T>(key: string): Promise<T | null> {
    if (!this.db) return null;
    const tx = this.db.transaction('meta', 'readonly');
    const row = await idbRequest<{ key: string; value: T } | undefined>(tx.objectStore('meta').get(key));
    return row ? row.value : null;
  }

  private async setMeta<T>(key: string, value: T): Promise<void> {
    if (!this.db) return;
    const tx = this.db.transaction('meta', 'readwrite');
    tx.objectStore('meta').put({ key, value });
    await idbTransactionDone(tx);
  }

  public async refreshCounts(): Promise<void> {
    if (!this.db) return;
    const tx = this.db.transaction(['outbox', 'words', 'meta'], 'readonly');
    const outboxAll = await idbRequest<OutboxRecord[]>(tx.objectStore('outbox').getAll());
    const wordsCount = await idbRequest<number>(tx.objectStore('words').count());
    const lastSyncRow = await idbRequest<{ key: string; value: number } | undefined>(
      tx.objectStore('meta').get('last_synced_at')
    );

    let pendingWords = 0;
    let pendingPreferences = 0;
    let failed = 0;
    for (const item of outboxAll) {
      if (item.error) {
        failed += 1;
      } else if (item.entity_type === 'word_progress') {
        pendingWords += 1;
      } else if (item.entity_type === 'preferences') {
        pendingPreferences += 1;
      }
    }

    this.state.pendingWords = pendingWords;
    this.state.pendingPreferences = pendingPreferences;
    this.state.failed = failed;
    this.state.totalWords = wordsCount;
    if (lastSyncRow?.value) {
      this.state.lastSyncedAt = lastSyncRow.value;
    }
    this.recomputeStatus();
  }

  private broadcastChanged(event?: string) {
    this.channel?.postMessage({
      type: 'changed',
      event: event || 'sync',
      timestamp: Date.now(),
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('geobu-sync-changed', { detail: { event } }));
    }
  }

  public async syncNow(): Promise<void> {
    if (!this.db) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.state.connection = 'offline';
      this.recomputeStatus();
      return;
    }
    const bootstrapped = (await this.getMeta<number>('sync_data_version')) === SYNC_DATA_VERSION;
    if (!bootstrapped) {
      await this.bootstrap();
    } else {
      await this.flush();
      await this.poll();
    }
  }

  /**
   * Initial Bootstrap: Downloads full word database, preferences, and catalogs into IndexedDB,
   * preserving any local outbox mutations via 3-way merge.
   */
  public async bootstrap(): Promise<void> {
    if (!this.db) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.state.connection = 'offline';
      this.recomputeStatus();
      return;
    }

    if (this.state.bootstrapping) return;
    const database = this.db;
    const owner = this.currentUserId;
    this.state.bootstrapping = true;
    this.state.error = '';
    this.recomputeStatus();

    const abortController = new AbortController();
    this.activeRequestAbort = abortController;
    let snapshotTransaction: IDBTransaction | undefined;

    try {
      let offset = 0;
      const limit = 500;
      let highWatermark: number | null = null;
      let hasMore = true;
      let serverTime = new Date().toISOString();
      let maxWordId = 0;
      let scopeVersion = '';
      let previous: any;
      const seen = new Set<number>();
      const allIncomingWords: SyncWordRecord[] = [];
      let incomingPrefs: SyncPreferencesRecord | null = null;
      let incomingCatalogs: SyncCatalogs | null = null;

      while (hasMore) {
        const page = await api.syncBootstrap(
          { limit, offset, snapshot_id: previous?.snapshot_id }, abortController.signal
        );
        validateSnapshotPage(page, offset, previous);
        if (page.scope_version !== `user:${owner}`) throw new Error('Account mismatch');
        for (const word of page.words) {
          if (seen.has(word.id)) throw new Error('Duplicate vocabulary record');
          seen.add(word.id);
        }
        allIncomingWords.push(...page.words);
        previous = page;
        incomingPrefs = page.preferences;
        incomingCatalogs = page.catalogs;
        highWatermark = page.high_watermark;
        serverTime = page.server_time;
        maxWordId = page.max_word_id;
        scopeVersion = page.scope_version;
        hasMore = page.has_more;
        offset = page.next_offset;
      }
      if (allIncomingWords.length !== previous.total_words) throw new Error('Incomplete vocabulary snapshot');
      if (this.db !== database || this.currentUserId !== owner) return;

      if (!this.db) return;
      const tx = this.db.transaction(
        ['words', 'remote_words', 'preferences', 'remote_preferences', 'catalogs', 'outbox', 'meta'],
        'readwrite'
      );
      snapshotTransaction = tx;
      const outboxStore = tx.objectStore('outbox');
      const pendingAll = await idbRequest<OutboxRecord[]>(outboxStore.getAll());
      const pendingByEntity = new Map<string, OutboxRecord>();
      for (const p of pendingAll) {
        pendingByEntity.set(`${p.entity_type}:${p.entity_id}`, p);
      }

      const wordsStore = tx.objectStore('words');
      const remoteWordsStore = tx.objectStore('remote_words');

      // A complete empty snapshot also removes deleted vocabulary.
      {
        wordsStore.clear();
        remoteWordsStore.clear();
        for (const remoteWord of allIncomingWords) {
          const normalized = this.normalizeWordRecord(remoteWord);
          remoteWordsStore.put(cloneValue(normalized));
          const pending = pendingByEntity.get(`word_progress:${normalized.id}`);
          if (pending && pending.value) {
            const merged = this.normalizeWordRecord(
              mergeLocalOverRemote(pending.base || {}, normalized, pending.value)
            );
            validateWord(merged);
            wordsStore.put(merged);
          } else {
            wordsStore.put(cloneValue(normalized));
          }
        }
      }

      for (const item of pendingAll) {
        if (item.entity_type === 'word_progress' && !seen.has(Number(item.entity_id))) outboxStore.delete(item.mutation_id);
      }
      if (incomingPrefs) {
        const prefStore = tx.objectStore('preferences');
        const remotePrefStore = tx.objectStore('remote_preferences');
        const normalizedPref: SyncPreferencesRecord = {
          ...incomingPrefs,
          id: 'user_preferences',
        };
        remotePrefStore.put(cloneValue(normalizedPref));
        const pendingPref = pendingByEntity.get('preferences:user_preferences');
        if (pendingPref && pendingPref.value) {
          const mergedPref = mergeLocalOverRemote<SyncPreferencesRecord>(
            pendingPref.base || {},
            normalizedPref,
            pendingPref.value
          );
          mergedPref.id = 'user_preferences';
          prefStore.put(mergedPref);
        } else {
          prefStore.put(cloneValue(normalizedPref));
        }
      }

      if (incomingCatalogs) {
        tx.objectStore('catalogs').put({ key: 'main', value: incomingCatalogs });
      }

      const nowMs = Date.now();
      const metaStore = tx.objectStore('meta');
      metaStore.put({ key: 'bootstrapped', value: 1 });
      metaStore.put({ key: 'sync_data_version', value: SYNC_DATA_VERSION });
      metaStore.put({ key: 'word_cursor', value: previous.word_cursor });
      metaStore.put({ key: 'checkpoint', value: highWatermark ?? 1 });
      metaStore.put({ key: 'since_time', value: serverTime });
      metaStore.put({ key: 'max_word_id', value: maxWordId });
      metaStore.put({ key: 'scope_version', value: scopeVersion || 'default' });
      metaStore.put({ key: 'last_synced_at', value: nowMs });

      await idbTransactionDone(tx);

      this.state.connection = 'online';
      this.state.lastSyncedAt = nowMs;
      this.state.error = '';
      this.retryDelay = INITIAL_RETRY_MS;
      await this.refreshCounts();
      this.broadcastChanged('bootstrap');
    } catch (err: any) {
      try { snapshotTransaction?.abort(); } catch {}
      if (err?.name === 'AbortError') return;
      if (err?.status === 401) {
        this.state.connection = 'auth_required';
        this.state.error = 'Authentication required';
      } else {
        this.state.connection = typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'unreachable';
        this.state.error = err?.message || 'Unable to synchronize with server';
        this.retryDelay = Math.min(MAX_RETRY_MS, this.retryDelay * 2);
      }
    } finally {
      this.state.bootstrapping = false;
      this.recomputeStatus();
    }
  }

  /**
   * Incremental Delta Poll: Pulls changes made on other devices or in Telegram Bot,
   * applying a 3-way merge over any pending local mutations.
   */
  public async poll(): Promise<void> {
    if (!this.db || this.polling || this.state.bootstrapping) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.state.connection = 'offline';
      this.recomputeStatus();
      return;
    }

    if ((await this.getMeta<number>('sync_data_version')) !== SYNC_DATA_VERSION) { await this.bootstrap(); return; }
    const database = this.db;
    const owner = this.currentUserId;
    this.polling = true;
    this.recomputeStatus();

    try {
      let hasMore = true;
      while (hasMore) {
        const checkpoint = (await this.getMeta<number>('checkpoint')) ?? 0;
        const wordCursor = await this.getMeta<string>('word_cursor');
        if (!wordCursor) throw new Error('Vocabulary snapshot required');
        const sinceTime = await this.getMeta<string>('since_time');
        const maxWordId = (await this.getMeta<number>('max_word_id')) ?? 0;

        const abortController = new AbortController();
        this.activeRequestAbort = abortController;

        const res = await api.syncChanges(
          {
            after_sync_version: checkpoint,
            word_cursor: wordCursor,
            since_time: sinceTime,
            max_word_id: maxWordId,
            limit: 250,
          },
          abortController.signal
        );

        if (res.reset_required) {
          this.polling = false;
          await this.bootstrap();
          return;
        }

        if (this.db !== database || this.currentUserId !== owner) return;
        validateDeltaPage(res, wordCursor);
        if (res.sync_protocol !== 2 || !res.word_cursor || !Array.isArray(res.changes) ||
            res.scope_version !== `user:${owner}` || (res.has_more && res.word_cursor === wordCursor))
          throw new Error('Invalid vocabulary changes');
        const changes = res.preferences ? [...res.changes, {
          entity_type: 'preferences' as const, operation: 'upsert' as const,
          entity_id: 'user_preferences', sync_version: res.next_sync_version,
          payload: res.preferences,
        }] : res.changes;
        const nowMs = Date.now();
        await this.applyRemoteChanges(changes, {
          word_cursor: res.word_cursor, checkpoint: res.next_sync_version ?? checkpoint,
          since_time: res.server_time, last_synced_at: nowMs,
        }, res.catalogs);
        this.state.lastSyncedAt = nowMs;
        hasMore = Boolean(res.has_more);
      }

      this.state.connection = 'online';
      this.state.error = '';
      this.retryDelay = INITIAL_RETRY_MS;
      await this.refreshCounts();
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      if (err?.status === 401) {
        this.state.connection = 'auth_required';
        this.state.error = 'Authentication required';
      } else if (err?.status === 404) {
        this.state.connection = 'unreachable';
        this.state.error = 'Vocabulary synchronization is unavailable';
      } else {
        this.state.connection = typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'unreachable';
        this.state.error = err?.message || 'Sync poll failed';
        this.retryDelay = Math.min(MAX_RETRY_MS, this.retryDelay * 2);
      }
    } finally {
      this.polling = false;
      this.recomputeStatus();
    }
  }

  private async applyRemoteChanges(changes: SyncChangeEvent[], metadata: Record<string, any> = {}, catalogs?: SyncCatalogs): Promise<void> {
    if (!this.db) return;
    const tx = this.db.transaction(
      ['words', 'remote_words', 'preferences', 'remote_preferences', 'outbox', 'tombstones', 'meta', 'catalogs'],
      'readwrite'
    );
    const wordsStore = tx.objectStore('words');
    const remoteWordsStore = tx.objectStore('remote_words');
    const prefStore = tx.objectStore('preferences');
    const remotePrefStore = tx.objectStore('remote_preferences');
    const outboxStore = tx.objectStore('outbox');

    const pendingAll = await idbRequest<OutboxRecord[]>(outboxStore.getAll());
    const pendingByKey = new Map<string, OutboxRecord>();
    for (const p of pendingAll) {
      pendingByKey.set(`${p.entity_type}:${p.entity_id}`, p);
    }

    try {
    for (const change of changes) {
      if (!['word', 'word_progress', 'preferences'].includes(change.entity_type) || !['upsert', 'delete'].includes(change.operation)) throw new Error('Invalid sync event');
      if (change.entity_type === 'word' || change.entity_type === 'word_progress') {
        const wid = Number(change.entity_id || change.payload?.id || change.payload?.word_id);
        if (!Number.isSafeInteger(wid) || wid <= 0) throw new Error('Invalid word ID');

        if (change.operation === 'delete') {
          wordsStore.delete(wid); remoteWordsStore.delete(wid);
          for (const item of pendingAll) if (item.entity_type === 'word_progress' && Number(item.entity_id) === wid) outboxStore.delete(item.mutation_id);
          continue;
        }
        const existingRemote = await idbRequest<SyncWordRecord | undefined>(remoteWordsStore.get(wid));
        if (!metadata.word_cursor && existingRemote?.sync_version && change.sync_version && change.sync_version < existingRemote.sync_version) {
          continue;
        }

        const candidate = { ...(existingRemote || {}), ...(change.payload || {}), id: wid, word_id: wid, sync_version: change.sync_version };
        validateWord(candidate);
        const remoteWord = this.normalizeWordRecord(candidate);
        validateWord(remoteWord);
        remoteWordsStore.put(cloneValue(remoteWord));

        const pending = pendingByKey.get(`word_progress:${wid}`);
        if (pending && pending.value) {
          // 3-Way Merge: keep pending.base untouched while unacknowledged, compute optimistic local view
          const optimistic = this.normalizeWordRecord(
            mergeLocalOverRemote(pending.base || {}, remoteWord, pending.value)
          );
          validateWord(optimistic);
          wordsStore.put(optimistic);
        } else {
          wordsStore.put(cloneValue(remoteWord));
        }
      } else if (change.entity_type === 'preferences') {
        const existingRemotePref = await idbRequest<SyncPreferencesRecord | undefined>(
          remotePrefStore.get('user_preferences')
        );
        const remotePref: SyncPreferencesRecord = {
          ...(existingRemotePref || {}),
          ...(change.payload as SyncPreferencesRecord),
          id: 'user_preferences',
          sync_version: change.sync_version,
        };
        remotePrefStore.put(cloneValue(remotePref));

        const pendingPref = pendingByKey.get('preferences:user_preferences');
        if (pendingPref && pendingPref.value) {
          const optimisticPref = mergeLocalOverRemote<SyncPreferencesRecord>(
            pendingPref.base || {},
            remotePref,
            pendingPref.value
          );
          optimisticPref.id = 'user_preferences';
          prefStore.put(optimisticPref);
        } else {
          prefStore.put(cloneValue(remotePref));
        }
      }
    }

    for (const [key,value] of Object.entries(metadata)) tx.objectStore('meta').put({ key, value });
    if (catalogs) tx.objectStore('catalogs').put({ key: 'main', value: catalogs });
    await idbTransactionDone(tx);
    } catch (error) { try { tx.abort(); } catch {} throw error; }
    this.broadcastChanged('remote_changes');
  }

  /**
   * Outbox Flush: Pushes local offline/optimistic mutations to `/api/web/sync/mutations`
   * with generation safety and server-side 3-way merge reconciliation.
   */
  public async flush(): Promise<void> {
    if (!this.db) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.state.connection = 'offline';
      this.recomputeStatus();
      return;
    }
    if (this.flushing) {
      this.flushAgain = true;
      return;
    }

    this.flushing = true;
    this.recomputeStatus();
    let applyTransaction: IDBTransaction | undefined;

    try {
      while (this.db) {
        this.flushAgain = false;
        const txRead = this.db.transaction('outbox', 'readonly');
        const allOutbox = await idbRequest<OutboxRecord[]>(txRead.objectStore('outbox').getAll());
        const eligible = allOutbox
          .filter((item) => !item.error)
          .sort((a, b) => a.created_at - b.created_at);

        if (eligible.length === 0) break;

        const batch = selectMutationBatch(eligible);
        if (batch.length === 0) break;

        // Freeze submitted snapshot for wire idempotency
        const txSnap = this.db.transaction('outbox', 'readwrite');
        const outboxSnapStore = txSnap.objectStore('outbox');
        const wireBatch: Array<Record<string, any>> = [];

        for (const item of batch) {
          const submitted = item.submitted || {
            value: cloneValue(item.value),
            base: cloneValue(item.base),
            operation: item.operation,
            generation: item.generation ?? 1,
          };
          item.submitted = submitted;
          item.attempts = (item.attempts || 0) + 1;
          outboxSnapStore.put(item);
          wireBatch.push({
            mutation_id: item.mutation_id,
            entity_type: item.entity_type,
            operation: submitted.operation,
            entity_id: item.entity_id,
            base: submitted.base,
            value: submitted.value,
            created_at: item.created_at,
          });
        }
        await idbTransactionDone(txSnap);

        const abortController = new AbortController();
        this.activeRequestAbort = abortController;

        let responseResults: Array<Record<string, any>> = [];
        try {
          const res = await api.syncMutations(wireBatch, abortController.signal);
          responseResults = res.results || [];
        } catch (mutErr: any) {
          if (mutErr?.status === 404) {
            // Legacy mock fallback if /api/web/sync/mutations is not mocked in older test
            for (const wItem of wireBatch) {
              if (wItem.entity_type === 'preferences' && wItem.value) {
                await api.updateUserPreferences(wItem.value).catch(() => null);
              }
              responseResults.push({
                mutation_id: wItem.mutation_id,
                status: 'applied',
                entity: wItem.value,
                sync_version: 1,
              });
            }
          } else {
            throw mutErr;
          }
        }

        if (!this.db) break;
        const txApply = this.db.transaction(
          ['words', 'remote_words', 'preferences', 'remote_preferences', 'outbox', 'meta'],
          'readwrite'
        );
        const wordsStore = txApply.objectStore('words');
        const remoteWordsStore = txApply.objectStore('remote_words');
        const prefStore = txApply.objectStore('preferences');
        const remotePrefStore = txApply.objectStore('remote_preferences');
        applyTransaction = txApply;
        const outboxApplyStore = txApply.objectStore('outbox');

        for (const result of responseResults) {
          const current = await idbRequest<OutboxRecord | undefined>(
            outboxApplyStore.get(result.mutation_id)
          );
          if (!current) continue;

          if (result.status === 'applied') {
            const submittedGen = current.submitted?.generation ?? 1;
            const currentGen = current.generation ?? 1;
            const canonical = result.entity;
            if (!canonical || typeof canonical !== 'object') throw new Error('Missing canonical mutation acknowledgement');

            if (current.entity_type === 'word_progress') {
              const wid = Number(current.entity_id);
              const existingRemote = await idbRequest<SyncWordRecord | undefined>(remoteWordsStore.get(wid));
              const candidate = { ...(existingRemote || {}), ...canonical, id: wid, word_id: wid };
              validateWord(candidate);
              const canonicalWord = this.normalizeWordRecord(candidate);
              remoteWordsStore.put(cloneValue(canonicalWord));

              if (currentGen !== submittedGen && current.value) {
                // Reconcile newer in-flight local generation against canonical server response
                const merged = this.normalizeWordRecord(
                  mergeLocalOverRemote(current.submitted?.value || {}, canonicalWord, current.value)
                );
                wordsStore.put(merged);
                outboxApplyStore.delete(current.mutation_id);
                const followUpId = generateUuid();
                outboxApplyStore.put({
                  mutation_id: followUpId,
                  entity_type: 'word_progress',
                  operation: 'update',
                  entity_id: wid,
                  base: cloneValue(canonicalWord),
                  value: cloneValue(merged),
                  created_at: Date.now(),
                  attempts: 0,
                  generation: 1,
                });
                this.flushAgain = true;
              } else {
                wordsStore.put(cloneValue(canonicalWord));
                outboxApplyStore.delete(current.mutation_id);
              }
            } else if (current.entity_type === 'preferences') {
              const canonicalPref: SyncPreferencesRecord = {
                ...(canonical as SyncPreferencesRecord),
                id: 'user_preferences',
              };
              remotePrefStore.put(cloneValue(canonicalPref));

              if (currentGen !== submittedGen && current.value) {
                const mergedPref = mergeLocalOverRemote<SyncPreferencesRecord>(
                  current.submitted?.value || {},
                  canonicalPref,
                  current.value
                );
                mergedPref.id = 'user_preferences';
                prefStore.put(mergedPref);
                outboxApplyStore.delete(current.mutation_id);
                outboxApplyStore.put({
                  mutation_id: generateUuid(),
                  entity_type: 'preferences',
                  operation: 'update',
                  entity_id: 'user_preferences',
                  base: cloneValue(canonicalPref),
                  value: cloneValue(mergedPref),
                  created_at: Date.now(),
                  attempts: 0,
                  generation: 1,
                });
                this.flushAgain = true;
              } else {
                prefStore.put(cloneValue(canonicalPref));
                outboxApplyStore.delete(current.mutation_id);
              }
            }
          } else if (result.status === 'retry_later') {
            delete current.submitted;
            outboxApplyStore.put(current);
          } else {
            current.error = result.detail || 'Synchronization failed for this change';
            outboxApplyStore.put(current);
          }
        }

        const nowMs = Date.now();
        txApply.objectStore('meta').put({ key: 'last_synced_at', value: nowMs });
        await idbTransactionDone(txApply);
        this.state.lastSyncedAt = nowMs;
        this.state.connection = 'online';
        this.state.error = '';

        if (!this.flushAgain) break;
      }

      await this.refreshCounts();
      this.broadcastChanged('flush');
    } catch (err: any) {
      try { applyTransaction?.abort(); } catch {}
      if (err?.name === 'AbortError') return;
      if (err?.status === 401) {
        this.state.connection = 'auth_required';
        this.state.error = 'Authentication required';
      } else {
        this.state.connection = typeof navigator !== 'undefined' && !navigator.onLine ? 'offline' : 'unreachable';
        this.state.error = err?.message || 'Failed to push local changes';
        this.retryDelay = Math.min(MAX_RETRY_MS, this.retryDelay * 2);
      }
    } finally {
      this.flushing = false;
      this.recomputeStatus();
    }
  }

  /**
   * Optimistically updates a word's learning progress (`stars`, `is_learned`, `postponed_date`)
   * in IndexedDB and queues a 3-way merge mutation in `outbox`.
   */
  public async saveWordProgress(
    wordId: number,
    patch: Partial<Pick<SyncWordRecord, 'stars' | 'is_learned' | 'postponed_date'>>,
    fallbackCardData?: Partial<SyncWordRecord>
  ): Promise<SyncWordRecord | null> {
    if (!this.db) return null;

    const tx = this.db.transaction(['words', 'remote_words', 'outbox'], 'readwrite');
    const wordsStore = tx.objectStore('words');
    const remoteWordsStore = tx.objectStore('remote_words');
    const outboxStore = tx.objectStore('outbox');

    const existingLocal = await idbRequest<SyncWordRecord | undefined>(wordsStore.get(wordId));
    const existingRemote = await idbRequest<SyncWordRecord | undefined>(remoteWordsStore.get(wordId));

    const baseRecord: SyncWordRecord = this.normalizeWordRecord(
      existingRemote ||
        existingLocal || {
          id: wordId,
          word_id: wordId,
          dictionary_id: fallbackCardData?.dictionary_id || 1,
          language_code: fallbackCardData?.language_code || 'ka',
          word: fallbackCardData?.word || '',
          short_word: fallbackCardData?.word || '',
          translation: fallbackCardData?.translation || '',
          phonetics: fallbackCardData?.phonetics || '',
          stars: fallbackCardData?.stars ?? 3,
          is_learned: false,
          postponed_date: null,
        }
    );

    const currentLocal: SyncWordRecord = this.normalizeWordRecord(existingLocal || baseRecord);
    const updatedLocal: SyncWordRecord = this.normalizeWordRecord({
      ...currentLocal,
      ...patch,
      updated_at: new Date().toISOString(),
    });

    wordsStore.put(cloneValue(updatedLocal));

    // Check if an outbox mutation already exists for this word
    const allOutbox = await idbRequest<OutboxRecord[]>(outboxStore.getAll());
    const existingOutbox = allOutbox.find(
      (item) => item.entity_type === 'word_progress' && Number(item.entity_id) === wordId
    );

    const baseProgress = existingOutbox?.base || {
      id: baseRecord.id,
      word_id: baseRecord.word_id,
      dictionary_id: baseRecord.dictionary_id,
      language_code: baseRecord.language_code,
      word: baseRecord.word,
      stars: baseRecord.stars,
      is_learned: baseRecord.is_learned,
      postponed_date: baseRecord.postponed_date ?? null,
    };

    const nextValue = {
      id: updatedLocal.id,
      word_id: updatedLocal.word_id,
      dictionary_id: updatedLocal.dictionary_id,
      language_code: updatedLocal.language_code,
      word: updatedLocal.word,
      stars: updatedLocal.stars,
      is_learned: updatedLocal.is_learned,
      postponed_date: updatedLocal.postponed_date ?? null,
    };

    if (existingOutbox) {
      existingOutbox.value = nextValue;
      existingOutbox.generation = (existingOutbox.generation ?? 1) + 1;
      delete existingOutbox.error;
      outboxStore.put(existingOutbox);
    } else if (!deepEqual(baseProgress, nextValue)) {
      outboxStore.put({
        mutation_id: generateUuid(),
        entity_type: 'word_progress',
        operation: 'update',
        entity_id: wordId,
        base: cloneValue(baseProgress),
        value: cloneValue(nextValue),
        created_at: Date.now(),
        attempts: 0,
        generation: 1,
      });
    }

    await idbTransactionDone(tx);
    await this.refreshCounts();
    this.channel?.postMessage({ type: 'outbox' });
    this.broadcastChanged('word_progress');

    if (this.isLeader && this.state.connection !== 'offline') {
      void this.flush();
    }
    return updatedLocal;
  }

  /**
   * Optimistically updates user preferences in IndexedDB and queues a 3-way merge mutation in `outbox`.
   */
  public async savePreferences(
    patch: Partial<SyncPreferencesRecord>
  ): Promise<SyncPreferencesRecord | null> {
    if (!this.db) return null;

    const tx = this.db.transaction(['preferences', 'remote_preferences', 'outbox'], 'readwrite');
    const prefStore = tx.objectStore('preferences');
    const remotePrefStore = tx.objectStore('remote_preferences');
    const outboxStore = tx.objectStore('outbox');

    const existingLocal = await idbRequest<SyncPreferencesRecord | undefined>(
      prefStore.get('user_preferences')
    );
    const existingRemote = await idbRequest<SyncPreferencesRecord | undefined>(
      remotePrefStore.get('user_preferences')
    );

    const defaultPrefs: SyncPreferencesRecord = {
      id: 'user_preferences',
      transcription: 'ipa',
      translation: 'en',
      selected_dictionaries: ['ka'],
      last_language: 'ka',
      auto_play_audio: true,
      daily_goal: 20,
      displayed_words_list: [],
      postpone_date_texts: ['tomorrow', 'in 3 days', 'in 7 days'],
    };

    const basePrefs: SyncPreferencesRecord = cloneValue(existingRemote || existingLocal || defaultPrefs);
    const currentLocal: SyncPreferencesRecord = cloneValue(existingLocal || basePrefs);
    const updatedLocal: SyncPreferencesRecord = {
      ...currentLocal,
      ...patch,
      id: 'user_preferences',
    };

    prefStore.put(cloneValue(updatedLocal));

    const allOutbox = await idbRequest<OutboxRecord[]>(outboxStore.getAll());
    const existingOutbox = allOutbox.find(
      (item) => item.entity_type === 'preferences' && item.entity_id === 'user_preferences'
    );

    const baseSnapshot = existingOutbox?.base || cloneValue(basePrefs);

    if (existingOutbox) {
      existingOutbox.value = cloneValue(updatedLocal);
      existingOutbox.generation = (existingOutbox.generation ?? 1) + 1;
      delete existingOutbox.error;
      outboxStore.put(existingOutbox);
    } else if (!deepEqual(baseSnapshot, updatedLocal)) {
      outboxStore.put({
        mutation_id: generateUuid(),
        entity_type: 'preferences',
        operation: 'update',
        entity_id: 'user_preferences',
        base: cloneValue(baseSnapshot),
        value: cloneValue(updatedLocal),
        created_at: Date.now(),
        attempts: 0,
        generation: 1,
      });
    }

    await idbTransactionDone(tx);
    await this.refreshCounts();
    this.channel?.postMessage({ type: 'outbox' });
    this.broadcastChanged('preferences');

    if (this.isLeader && this.state.connection !== 'offline') {
      void this.flush();
    }
    return updatedLocal;
  }

  public async getAllWords(): Promise<SyncWordRecord[]> {
    if (!this.db) return [];
    const tx = this.db.transaction('words', 'readonly');
    const all = await idbRequest<SyncWordRecord[]>(tx.objectStore('words').getAll());
    return all.sort((a, b) => a.id - b.id);
  }

  public async getWord(wordId: number): Promise<SyncWordRecord | null> {
    if (!this.db) return null;
    const tx = this.db.transaction('words', 'readonly');
    const row = await idbRequest<SyncWordRecord | undefined>(tx.objectStore('words').get(wordId));
    return row || null;
  }

  public async upsertWordsLocal(words: SyncWordRecord[]): Promise<void> {
    if (!this.db || words.length === 0) return;
    const tx = this.db.transaction(['words', 'remote_words'], 'readwrite');
    const wordsStore = tx.objectStore('words');
    const remoteWordsStore = tx.objectStore('remote_words');
    for (const w of words) {
      const norm = this.normalizeWordRecord(w);
      wordsStore.put(cloneValue(norm));
      remoteWordsStore.put(cloneValue(norm));
    }
    await idbTransactionDone(tx);
    await this.refreshCounts();
  }

  public async getPreferences(): Promise<SyncPreferencesRecord> {
    const fallback: SyncPreferencesRecord = {
      id: 'user_preferences',
      transcription: 'ipa',
      translation: 'en',
      selected_dictionaries: ['ka'],
      last_language: 'ka',
      auto_play_audio: true,
      daily_goal: 20,
      displayed_words_list: [],
      postpone_date_texts: ['tomorrow', 'in 3 days', 'in 7 days'],
    };
    if (!this.db) return fallback;
    const tx = this.db.transaction('preferences', 'readonly');
    const row = await idbRequest<SyncPreferencesRecord | undefined>(
      tx.objectStore('preferences').get('user_preferences')
    );
    return row ? { ...fallback, ...row } : fallback;
  }

  public async getCatalogs(): Promise<SyncCatalogs> {
    const fallback: SyncCatalogs = {
      available_languages: [
        { code: 'en', name: '🇺🇸 English' },
        { code: 'ru', name: '🇷🇺 Russian' },
        { code: 'es', name: '🇪🇸 Spanish' },
        { code: 'ka', name: '🇬🇪 Georgian' },
      ],
      available_transcriptions: [
        { code: 'ipa', name: '🗣️ IPA Broad (Standard)' },
        { code: 'ipa_narrow', name: '🗣️ IPA Narrow (Detailed)' },
        { code: 'en_rs', name: '🔤 English Phonetics Respelling' },
        { code: 'ru_rs', name: '🔤 Russian Phonetics Respelling' },
        { code: 'lat', name: '🔤 Latin Transliteration' },
        { code: '-', name: '🙅 No Transcription' },
      ],
      available_dictionaries: [
        { id: 'ka', code: 'ka', name: 'Georgian Basic' },
        { id: 'es', code: 'es', name: 'Spanish Core' },
      ],
      parts_of_speech: [
        { id: 1, name: 'Noun' },
        { id: 2, name: 'Verb' },
        { id: 3, name: 'Adjective' },
        { id: 4, name: 'Adverb' },
      ],
    };
    if (!this.db) return fallback;
    const tx = this.db.transaction('catalogs', 'readonly');
    const row = await idbRequest<{ key: string; value: SyncCatalogs } | undefined>(
      tx.objectStore('catalogs').get('main')
    );
    return row?.value ? { ...fallback, ...row.value } : fallback;
  }

  private normalizeWordRecord(raw: Partial<SyncWordRecord> & Record<string, any>): SyncWordRecord {
    const id = Number(raw.id ?? raw.word_id ?? 0);
    const stars = raw.stars !== undefined && raw.stars !== null ? Math.max(0, Math.min(5, Number(raw.stars))) : 3;
    const trMap = isPlainObject(raw.translations) ? (raw.translations as Record<string, string>) : {};
    const phMap = isPlainObject(raw.phonetics_map) ? (raw.phonetics_map as Record<string, string>) : {};
    const firstTr =
      raw.translation ||
      trMap.en ||
      trMap.ru ||
      Object.values(trMap)[0] ||
      '';
    const firstPh =
      raw.phonetics ||
      phMap.ipa ||
      phMap.en_rs ||
      phMap.ru_rs ||
      Object.values(phMap)[0] ||
      '';

    return {
      id,
      word_id: id,
      dictionary_id: Number(raw.dictionary_id || 1),
      language_code: String(raw.language_code || 'ka'),
      word: String(raw.word || raw.short_word || ''),
      short_word: String(raw.short_word || raw.word || ''),
      part_of_speech_id: raw.part_of_speech_id !== undefined && raw.part_of_speech_id !== null ? Number(raw.part_of_speech_id) : null,
      part_of_speech_name: raw.part_of_speech_name ? String(raw.part_of_speech_name) : null,
      translations: trMap,
      translation: String(firstTr),
      phonetics_map: phMap,
      phonetics: String(firstPh),
      hint: String(raw.hint || ''),
      stars,
      is_learned: Boolean(raw.is_learned),
      postponed_date: raw.postponed_date ? String(raw.postponed_date) : null,
      updated_at: raw.updated_at ? String(raw.updated_at) : null,
      sync_version: raw.sync_version !== undefined ? Number(raw.sync_version) : 1,
    };
  }
}

function isPlainObject(val: unknown): val is Record<string, any> {
  return Boolean(val) && typeof val === 'object' && !Array.isArray(val);
}

export const localSync = new LocalSyncService();
